import { useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  userSecuritiesAccountsServices,
  userSecuritiesAccountsSlice,
} from '!core/redux';
import {
  securitiesAccountsFullSelector,
  selectFavoriteSecurityAccount,
} from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { TrlScreenSummary, TrlTextWithLink } from '»components/atoms';
import { TrlGrid } from '»components/molecules/TrlGrid';
import { useHandleSagaError } from '»utils/customHooks';

import { columns } from './TrlSecuritiesAccounts.schema';
import { BoxStyled, TypographyStyled } from './TrlSecuritiesAccounts.styles';

export const TrlSecuritiesAccounts = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.up('md'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLarge = useMediaQuery(theme.breakpoints.up('xl'));

  const securityAccounts = useSelector(securitiesAccountsFullSelector);
  const favoriteSecurityAccount = useSelector(selectFavoriteSecurityAccount);
  const [favoriteValue, setFavoriteValue] = useState(favoriteSecurityAccount);
  const [selectedAccount, setSelectedAccount] = useState(
    securityAccounts.find(
      (securityAccount) => securityAccount.account === favoriteSecurityAccount,
    ) ?? securityAccounts[0],
  );

  const { handleSagaError } = useHandleSagaError();

  const handleFavoriteChange = (event) => {
    setFavoriteValue(event.target.value);
    userSecuritiesAccountsServices
      .setUserSecuritiesAccountsFavourite({
        account: event.target.value,
      })
      .then(() => {
        dispatch(
          userSecuritiesAccountsSlice.actions.setSelectedAccount(
            event.target.value,
          ),
        );
        dispatch(
          userSecuritiesAccountsSlice.actions.userSecuritiesAccountsRequest(),
        );
      })
      .catch((error) => {
        handleSagaError(error);
      });
  };

  const handleSelectedAccount = (account) => {
    setSelectedAccount(account);
  };

  const handleVisibleChange = (event) => {
    userSecuritiesAccountsServices
      .setUserSecuritiesAccountsVisible({
        account: event.target.id,
        visible: event.target.value === 'Y' ? 'N' : 'Y',
      })
      .then(() => {
        dispatch(
          userSecuritiesAccountsSlice.actions.userSecuritiesAccountsRequest(),
        );
        dispatch(
          userSecuritiesAccountsSlice.actions.setSelectedAccountFavorite(),
        );
      })
      .catch((error) => {
        handleSagaError(error);
      });
  };

  return (
    <>
      {!isLarge ? (
        <TrlScreenSummary title="TRLT_UI-CUSTODIAN_ACCOUNTS" hasTopMargin>
          <TrlTextWithLink mainText="TRLT_UI-SECURITY_ACC_SELECTOR" />
        </TrlScreenSummary>
      ) : (
        <TrlTextWithLink mainText="TRLT_UI-SECURITY_ACC_SELECTOR" />
      )}
      <TrlGrid
        columns={columns({
          handleVisibleChange,
          favoriteValue,
          handleFavoriteChange,
          theme,
          isLarge,
          isMedium,
          isExtraLarge,
        })}
        data={securityAccounts}
        selectedItem={selectedAccount}
        selectedItemChange={handleSelectedAccount}
        dataItemKey="account"
        gridWidth={isLarge && '614px'}
      />
      {!isMedium && !isLarge && (
        <BoxStyled sx={{ display: 'flex' }}>
          <TypographyStyled className="selectedOptionDesc">
            <FormattedMessage id="TRLT_UI-DESCRIPTION" />:
          </TypographyStyled>
          <TypographyStyled>{selectedAccount.accountDescr}</TypographyStyled>
        </BoxStyled>
      )}
    </>
  );
};
